import React, { useState } from 'react';
import { AlertTriangle, Check, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

/**
 * Modal que exige al empleado aceptar o rechazar los cierres automáticos
 * pendientes. Es obligatorio por normativa (2026): el empleado debe validar
 * cualquier cambio o cierre no realizado por él mismo.
 *
 * Props:
 *   - cierres: [{ id, fecha, horas, hora_entrada, hora_fin }]
 *   - onResponder: async (cierre_id, aceptar, comentario) => void
 *   - onCerrar: () => void  (se llama cuando todos han sido respondidos)
 */
export default function AutoCierreAceptacionModal({ cierres, onResponder, onCerrar }) {
  const [idx, setIdx] = useState(0);
  const [modoRechazo, setModoRechazo] = useState(false);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);

  if (!cierres || cierres.length === 0) return null;
  const actual = cierres[idx];
  if (!actual) return null;

  const fmt = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Europe/Madrid',
    });
  };
  const fmtFecha = (iso) => {
    if (!iso) return '—';
    // fecha yyyy-mm-dd (sin hora)
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  const responder = async (aceptar) => {
    if (!aceptar && comentario.trim().length === 0) {
      toast.error('Indica un motivo para rechazar el cierre');
      return;
    }
    setEnviando(true);
    try {
      await onResponder(actual.id, aceptar, comentario.trim());
      // pasar al siguiente
      if (idx + 1 < cierres.length) {
        setIdx(idx + 1);
        setModoRechazo(false);
        setComentario('');
      } else {
        onCerrar();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar tu respuesta');
    } finally { setEnviando(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" data-testid="auto-cierre-modal">
      <div className="w-full max-w-md bg-slate-900 border border-amber-500/50 rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-amber-500/10 border-b border-amber-500/30 px-5 py-4 flex items-center gap-3">
          <AlertTriangle className="h-6 w-6 text-amber-400 shrink-0" />
          <div>
            <h2 className="font-heading text-lg font-bold text-white">Confirma tu jornada</h2>
            <p className="text-xs text-amber-200/80">
              {cierres.length > 1 ? `Cierre ${idx + 1} de ${cierres.length}` : 'Cerramos tu jornada porque olvidaste fichar la salida'}
            </p>
          </div>
        </div>

        <div className="p-5 space-y-4 text-sm text-slate-200">
          <p>
            Registramos automáticamente el fin de tu jornada. <strong>Por normativa, tienes que confirmar
            o rechazar</strong> este cambio.
          </p>

          <div className="rounded-xl bg-slate-800 border border-slate-700 p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-400 text-xs">Fecha</span>
              <span className="font-semibold" data-testid="auto-cierre-fecha">{fmtFecha(actual.fecha)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400 text-xs">Entrada</span>
              <span className="font-semibold tabular-nums">{fmt(actual.hora_entrada)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400 text-xs">Salida asumida</span>
              <span className="font-semibold tabular-nums">{fmt(actual.hora_fin)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-slate-700">
              <span className="text-slate-400 text-xs">Horas registradas</span>
              <span className="font-bold text-emerald-400" data-testid="auto-cierre-horas">{actual.horas} h</span>
            </div>
          </div>

          {!modoRechazo && (
            <div className="flex flex-col gap-2 pt-2">
              <Button onClick={() => responder(true)} disabled={enviando} data-testid="auto-cierre-aceptar"
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-12">
                {enviando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-5 w-5 mr-2" />}
                Sí, es correcto
              </Button>
              <Button variant="outline" onClick={() => setModoRechazo(true)} disabled={enviando}
                data-testid="auto-cierre-rechazar-abrir"
                className="w-full bg-slate-900 border-slate-600 text-slate-200 hover:bg-slate-800">
                <X className="h-5 w-5 mr-2" /> No es correcto
              </Button>
            </div>
          )}

          {modoRechazo && (
            <div className="space-y-3 pt-1">
              <label className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Motivo del rechazo</label>
              <Textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows={3}
                data-testid="auto-cierre-comentario"
                placeholder="Explica brevemente qué pasó (ej: seguí trabajando hasta las 20:00)."
                className="bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setModoRechazo(false); setComentario(''); }} disabled={enviando}
                  className="flex-1 bg-slate-900 border-slate-600 text-slate-200 hover:bg-slate-800">Cancelar</Button>
                <Button onClick={() => responder(false)} disabled={enviando || comentario.trim().length === 0}
                  data-testid="auto-cierre-rechazar-confirm"
                  className="flex-1 bg-red-600 hover:bg-red-700">
                  {enviando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Enviar rechazo
                </Button>
              </div>
            </div>
          )}

          <p className="text-[11px] text-slate-500 text-center pt-1">
            Tu respuesta quedará registrada en el sistema para que tu empresa la revise.
          </p>
        </div>
      </div>
    </div>
  );
}
